import React from "react";
import {
  ChartLineUp,
  GitBranch,
  Buildings,
  Brain,
  Stack,
  GraduationCap,
  Notebook,
  TestTube,
} from "@phosphor-icons/react";

// PHASE 1 — first 3 months. Theory / framework. Included in ALL packages.
const PHASE_1 = [
  {
    code: "M01",
    icon: ChartLineUp,
    title: "Market Profile — The Foundation",
    duration: "Month 1",
    points: [
      "TPO theory and the auction process",
      "Value Areas (VAH / VAL / POC)",
      "Initial Balance (IB), range extension, rotation",
      "Day types: trend, normal, neutral, double-distribution",
    ],
  },
  {
    code: "M02",
    icon: GitBranch,
    title: "Order Flow & Footprint",
    duration: "Month 2",
    points: [
      "Delta & Cumulative Volume Delta (CVD)",
      "Footprint reading: bid × ask",
      "Absorption, exhaustion, aggressive imbalances",
      "Trap moves and stop-runs",
    ],
  },
  {
    code: "M03",
    icon: Buildings,
    title: "Institutional Setups & Execution",
    duration: "Month 3",
    points: [
      "Open Drive / Open Auction / Open Test-Drive",
      "Value migration & responsive vs initiative trades",
      "Composite profiles for swing context",
      "GoCharting templates · entries · sizing · risk",
    ],
  },
];

// PHASE 2 — months 4–6. Application & assignment. GOLD ONLY.
const PHASE_2 = [
  {
    code: "P04",
    icon: Notebook,
    title: "Mark Your Levels",
    duration: "Month 4",
    points: [
      "Daily EOD level-drawing routine on Nifty / Bank Nifty / MCX",
      "Composite profile context for next-day plan",
      "Compare your levels against the mentor's read",
      "Build a reliable, repeatable preparation ritual",
    ],
  },
  {
    code: "P05",
    icon: TestTube,
    title: "Paper-Trade the Setups",
    duration: "Month 5",
    points: [
      "Forward-test MP + OF setups live with sim capital",
      "Journal every entry, exit and the reason",
      "Identify which setups suit your personality",
      "Coach-reviewed P&L every month",
    ],
  },
  {
    code: "P06",
    icon: GraduationCap,
    title: "Back-Test & Submit",
    duration: "Month 6 · Assignment",
    points: [
      "Back-test your top setup on 6 months of data",
      "Quantify edge: win rate, R:R, drawdown",
      "Produce a written results report",
      "Final review with Vishnu Sir before going live",
    ],
  },
];

const PSYCH = {
  code: "ON",
  icon: Brain,
  title: "Trading Psychology & Risk · Ongoing",
  duration: "Throughout",
  points: [
    "Loss recovery & tilt management",
    "Building a personal trading constitution",
    "Capital protection rules",
    "Monthly 1-on-1 P&L review (Gold & Bronze)",
  ],
};

function ModuleCard({ m, accent = "#D9381E" }) {
  const Icon = m.icon;
  return (
    <div
      data-testid={`module-${m.code}`}
      className="border border-[#0A0A0A] bg-[#F4F3ED] p-7 lg:p-8 hover-brutal"
    >
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <Icon size={26} weight="duotone" />
          <span className="font-mono-acc text-[10px] tracking-[0.3em] text-[#4A4A4A]">
            {m.code}
          </span>
        </div>
        <span
          className="font-mono-acc text-[10px] tracking-[0.2em] uppercase px-2 py-1 border"
          style={{ color: accent, borderColor: accent }}
        >
          {m.duration}
        </span>
      </div>
      <h3 className="font-display font-black text-xl lg:text-2xl tracking-tight mb-4">
        {m.title}
      </h3>
      <ul className="space-y-2.5">
        {m.points.map((p, i) => (
          <li key={i} className="flex gap-3 font-body text-sm text-[#4A4A4A]">
            <span className="font-mono-acc mt-0.5" style={{ color: accent }}>
              →
            </span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PhaseHeader({ tag, title, subtitle, badge }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-6">
      <div>
        <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-2">
          {tag}
        </div>
        <h3 className="font-display font-black text-2xl lg:text-4xl tracking-tighter leading-[0.95]">
          {title}
        </h3>
        <p className="font-body text-[#4A4A4A] mt-2 text-sm max-w-xl">{subtitle}</p>
      </div>
      {badge}
    </div>
  );
}

export default function Syllabus() {
  return (
    <section
      data-testid="syllabus-section"
      id="syllabus"
      className="py-20 lg:py-28 border-t border-[#D4D0C5]"
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-4">
              03 / SYLLABUS
            </div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
              Theory in 3 months.
              <br />
              Mastery in the next 3.
            </h2>
          </div>
          <div className="flex flex-col gap-4 max-w-md">
            <p className="font-body text-[#4A4A4A]">
              The framework comes first. Then we put it to work in real markets —
              mark levels, paper-trade, back-test. Theory without application is
              useless.
            </p>
            <a
              href="/downloads/GreatEagle_Syllabus.pdf"
              download
              data-testid="syllabus-download-btn"
              className="inline-flex items-center justify-center gap-3 self-start bg-[#0A0A0A] text-[#F4F3ED] px-6 py-3.5 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors"
            >
              ↓ Download full syllabus (PDF)
            </a>
          </div>
        </div>

        {/* PHASE 1 */}
        <div className="mb-14">
          <PhaseHeader
            tag="PHASE 01 / FIRST 3 MONTHS"
            title="Foundations & Framework"
            subtitle="Everything you need to read auctions, footprints and institutional intent. Included in every package."
            badge={
              <span className="font-mono-acc text-[10px] tracking-[0.25em] uppercase border border-[#0A0A0A] px-3 py-1.5 self-start">
                STARTER · CORE · PRO
              </span>
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {PHASE_1.map((m) => (
              <ModuleCard key={m.code} m={m} />
            ))}
          </div>
        </div>

        {/* PHASE 2 */}
        <div className="mb-14">
          <PhaseHeader
            tag="PHASE 02 / NEXT 3 MONTHS"
            title="Live Application & Assignment"
            subtitle="Move from chart-watcher to participant. Mark levels every EOD, forward-test in sim, back-test and submit a full results report as your final assignment."
            badge={
              <span className="font-mono-acc text-[10px] tracking-[0.25em] uppercase bg-[#D9381E] text-white px-3 py-1.5 self-start">
                GOLD ONLY
              </span>
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {PHASE_2.map((m) => (
              <ModuleCard key={m.code} m={m} />
            ))}
          </div>
        </div>

        {/* ONGOING — psychology */}
        <div>
          <div className="grid grid-cols-1">
            <ModuleCard m={PSYCH} accent="#0A0A0A" />
          </div>
        </div>
      </div>
    </section>
  );
}
